/**
 * Copyright 2016 Taito United Oy
 * All rights reserved.
 */

import PropTypes from 'prop-types';
import React from 'react';
import { FormattedHTMLMessage, FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

// Components
import ReviewsCard from './ReviewsCard';
import ReviewsAddCard from './ReviewsAddCard';
import LoadingIndicator from '../../components/LoadingIndicator';

// Locale
import messages from './messages/ReviewsContainer_messages';

const propTypes = {
  reviews: PropTypes.array,
  review: PropTypes.object,
  report: PropTypes.object,
  locale: PropTypes.object.isRequired,
  user: PropTypes.object,
  session: PropTypes.object,
  saveReview: PropTypes.func.isRequired,
  onLogin: PropTypes.func.isRequired,
  startChat: PropTypes.func.isRequired,
};

const defaultProps = {
  reviews: null,
  review: null,
  report: {},
  user: null,
  session: null,
};

const ReviewsContainer = ({
  report,
  review,
  locale,
  session,
  user,
  reviews,
  saveReview,
  onLogin,
  startChat,
}) => {
  let isAuthenticated = false;
  let isCompanyLoggedIn = false;

  if (session) {
    const { logins: { tilaajavastuu } } = session;
    if (tilaajavastuu) {
      const { client: { verified, company }} = tilaajavastuu;
      isAuthenticated = verified;
      // Check if logged in with Tilaajavastuu and company matches
      isCompanyLoggedIn = (
        !!company
        && (
          company.country === report.country
          && company.identifier === report.identifier
        )
      );
    }
  }

  const reportUrl = `/selvitys/${report.country}/${report.identifier}`;

  return (
    <Container>
      {(!reviews || (!report || !report.identifier) && !user)
        ? <LoadingIndicator />
        : (
          <React.Fragment>
            <Header>
              {!user ? (
                <React.Fragment>
                  <FormattedMessage
                    {...messages.headerCompanys}
                    info="&quot;Company s&quot;"
                  />
                  <strong>
                    <Link to={reportUrl}>
                      {` ${report.names.primary} `}
                    </Link>
                  </strong>
                  <FormattedMessage
                    {...messages.headerCompanyReviews}
                    info="&quot;reviews&quot;"
                  />
                </React.Fragment>
              ) : (
                <FormattedHTMLMessage
                  {...messages.headerTextUser}
                  info="Header text"
                  values={{ textLabel: user.name }}
                />
              )}
            </Header>

            <Cards>
              {(!!report.identifier && !review.overall) && (
                <Card>
                  <CardContainer>
                    <ReviewsAddCard
                      session={session}
                      report={report}
                      locale={locale}
                      onLogin={onLogin}
                      saveReview={saveReview}
                    />
                  </CardContainer>
                </Card>
              )}
              {reviews && reviews.map((element, index) => (
                <Card key={`company-review-${index}`}>
                  <CardContainer>
                    <ReviewsCard
                      review={element}
                      canSendMessage={
                        !!report && isCompanyLoggedIn && isAuthenticated
                      }
                      startChat={startChat}
                    />
                  </CardContainer>
                </Card>
              ))}
            </Cards>
          </React.Fragment>
        )
      }
    </Container>
  );
};

ReviewsContainer.propTypes = propTypes;
ReviewsContainer.defaultProps = defaultProps;

const Cards = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const Card = styled.div`
  background: #ffffff;
  box-sizing: border-box;
  height: 100%;
  padding: 6px;
  width: 100%;

  @media (min-width: 640px) {
    width: 50%;
  }

  @media (min-width: 1030px) {
    width: 25%;
  }
`;

const CardContainer = styled.div`
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  width: 100%;
`;

const Container = styled.div`
  margin: 0px auto;
  max-width: 1100px;
  width: 100%;
`;

const Header = styled.h1`
  text-align: center;
`;

export default ReviewsContainer;
